import { useEffect, useRef, useState } from "react";
import { useApiFetch } from "../../../api/client";
import { Chip } from "../../../components/ui/Chip";
import { errorMessage, recordedLabel } from "../../cockpit/format";
import { revisionLabel, withCurrentFlags, type TdsFileWithCurrent } from "./tdsFiles";
import type { GradeTdsFile, StoredFile } from "../types";

interface Props {
  gradeId: number;
  canWrite: boolean;
  reloadNumber: number;
  onChanged: () => void;
}

const SMALL_BUTTON_CLASSES =
  "bg-transparent border border-rs-jet-light rounded px-3 py-1 text-xs text-rs-off-white cursor-pointer hover:border-rs-green hover:text-rs-green disabled:opacity-50 disabled:cursor-not-allowed";
const LINK_BUTTON_CLASSES =
  "bg-transparent border-0 p-0 text-xs text-rs-green cursor-pointer hover:underline disabled:opacity-50 disabled:cursor-not-allowed";

export function GradeTdsFilesPanel({ gradeId, canWrite, reloadNumber, onChanged }: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [files, setFiles] = useState<GradeTdsFile[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmingRemoveId, setConfirmingRemoveId] = useState<number | null>(null);
  const [busyFileId, setBusyFileId] = useState<number | null>(null);

  useEffect(() => {
    let isStale = false;
    apiFetchRef
      .current(`nomenclature/grades/${gradeId}/tds`)
      .then((response) => response.json())
      .then((loadedFiles: GradeTdsFile[]) => {
        if (isStale) return;
        setFiles(loadedFiles);
        setMessage(null);
      })
      .catch((error) => {
        if (!isStale) setMessage(`Couldn't load the TDS files: ${errorMessage(error)}`);
      });
    return () => {
      isStale = true;
    };
  }, [gradeId, reloadNumber]);

  const openFile = async (file: GradeTdsFile) => {
    const newTab = window.open("", "_blank");
    try {
      const response = await apiFetchRef.current(`tds/files/${file.intakeFileId}`);
      const storedFile: StoredFile = await response.json();
      if (!storedFile.readUrl) throw new Error("No link came back for this file.");
      if (!newTab) {
        setMessage("Your browser blocked the new tab. Allow pop-ups for this site and try again.");
        return;
      }
      newTab.opener = null;
      newTab.location.href = storedFile.readUrl;
    } catch (error) {
      newTab?.close();
      setMessage(`Couldn't open ${file.fileName}: ${errorMessage(error)}`);
    }
  };

  const removeFile = async (file: GradeTdsFile) => {
    setBusyFileId(file.gradeTdsFileId);
    try {
      await apiFetchRef.current(`nomenclature/grade-tds/${file.gradeTdsFileId}`, { method: "DELETE" });
      setFiles((currentFiles) =>
        (currentFiles ?? []).filter((currentFile) => currentFile.gradeTdsFileId !== file.gradeTdsFileId),
      );
      setConfirmingRemoveId(null);
      setMessage(`Removed ${file.fileName} from this grade. It's kept in the history.`);
      onChanged();
    } catch (error) {
      setMessage(`Couldn't remove ${file.fileName}: ${errorMessage(error)}`);
    } finally {
      setBusyFileId(null);
    }
  };

  const fileDetails = (file: TdsFileWithCurrent) =>
    [
      file.sourceLabel,
      revisionLabel(file.revisionMonth),
      `Added by ${file.attachedByDisplayName} · ${recordedLabel(file.attachedUtc)}`,
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <div className="flex flex-col">
      {message && (
        <p className="m-0 mb-2 text-xs text-rs-off-white" role="status">
          {message}
        </p>
      )}
      {files === null ? (
        !message && <p className="m-0 text-xs text-rs-light-gray">Loading…</p>
      ) : files.length === 0 ? (
        <p className="m-0 text-xs text-rs-light-gray">No TDS files on this grade yet.</p>
      ) : (
        withCurrentFlags(files).map((file) => {
          const isBusy = busyFileId === file.gradeTdsFileId;
          return (
            <div
              key={file.gradeTdsFileId}
              className="flex flex-wrap justify-between gap-x-4 gap-y-1.5 py-2 border-t border-rs-jet first:border-t-0"
            >
              <div className="min-w-0">
                <span className="font-mono text-xs text-rs-off-white break-all">{file.fileName}</span>
                {file.isCurrent && (
                  <Chip variant="ok" className="ml-2">
                    Current
                  </Chip>
                )}
                <span className="block mt-0.5 text-xs text-rs-light-gray">{fileDetails(file)}</span>
              </div>
              <div className="flex items-center gap-2.5">
                <button type="button" className={SMALL_BUTTON_CLASSES} onClick={() => openFile(file)}>
                  View
                </button>
                {canWrite &&
                  (confirmingRemoveId === file.gradeTdsFileId ? (
                    <>
                      <span className="text-xs text-rs-light-gray">Remove from this grade?</span>
                      <button
                        type="button"
                        className={LINK_BUTTON_CLASSES}
                        disabled={isBusy}
                        onClick={() => removeFile(file)}
                      >
                        {isBusy ? "Removing…" : "Remove"}
                      </button>
                      <button
                        type="button"
                        className={LINK_BUTTON_CLASSES}
                        disabled={isBusy}
                        onClick={() => setConfirmingRemoveId(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={LINK_BUTTON_CLASSES}
                      onClick={() => setConfirmingRemoveId(file.gradeTdsFileId)}
                    >
                      Remove
                    </button>
                  ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
